import type { ReactNode } from 'react'

/**
 * Tabs — `DESIGN-HANDOVER.md` §5.6, Figma `128:4525`.
 * Variant `Pill | Underline` x Size `Medium (76x24) | Large (80x31)`.
 *
 * ⚠ THE ONE MEASURED EXCEPTION IN THE WHOLE COMPONENT SET. Every other "selected" component in
 * this design system uses the `#f4fafe` surface / `#bdc6f7` border pair. Tabs does not: its
 * selected state is near-black `#1a1a1a` text on `#ececec`. That is measured, not a sampling
 * slip, and it is pinned by a test in `__tests__/ui.test.tsx` precisely so a later
 * "consistency fix" cannot reconcile it away by inference. Ship it as measured.
 *
 * NOT THIS COMPONENT'S JOB, recorded so nobody does it by accident: this is meant to eventually
 * consolidate the three hand-rolled pickers that exist today — `export/SpecFilter.tsx`,
 * `CampaignDashboardPage.tsx`, and `SiteContextPreview.tsx`'s `SegButton`. Migrating those call
 * sites is a separate piece of work, not part of building the primitive.
 */
export type TabItem = {
  id: string
  label: ReactNode
  disabled?: boolean
}

export function Tabs({
  items,
  value,
  onChange,
  variant = 'pill',
  size = 'md',
  label,
  className,
}: {
  items: readonly TabItem[]
  value: string
  onChange: (id: string) => void
  variant?: 'pill' | 'underline'
  /** `md` = 24px tall (Figma Medium), `lg` = 31px tall (Figma Large). */
  size?: 'md' | 'lg'
  /** Accessible name for the tablist. */
  label?: string
  className?: string
}) {
  const height = size === 'lg' ? 'h-[31px]' : 'h-6'

  return (
    <div
      role="tablist"
      aria-label={label}
      className={`inline-flex items-center ${variant === 'pill' ? 'gap-small' : 'gap-none border-b border-border-primary'}${className ? ` ${className}` : ''}`}
    >
      {items.map((item) => {
        const selected = item.id === value
        const shape =
          variant === 'pill'
            ? // §5.6's measured table gives Unselected Hover surface `#f9f9f9`, which is
              // `surface-disabled-subtle` — NOT `surface-tertiary` (`#f2f2f2`), which this
              // used to carry. The token's role name says "disabled", but §5.6 measured this
              // exact value for a hover state, so the value is the evidence and the name is
              // just where the token happens to live. Selected Hover (`#e6e6e6`, §5.6) is
              // still MISSING: no surface token carries `#e6e6e6` except `surface-disabled`,
              // and borrowing that one would be a genuine role inversion — left unbuilt and
              // reported rather than faked.
              `rounded-150 ${selected ? 'bg-surface-tabs-selected' : 'bg-transparent hover:bg-surface-disabled-subtle'}`
            : `rounded-none border-b-2 ${selected ? 'border-icon-tabs-selected' : 'border-transparent hover:border-border-primary'}`

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={`inline-flex ${height} cursor-pointer items-center justify-center px-medium text-body-default-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:text-text-disabled ${shape} ${
              selected ? 'text-icon-tabs-selected' : 'text-text-secondary'
            }`}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
